import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, CircularProgress, Alert, Tabs, Tab,
  Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, LinearProgress,
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  DoneAll as MarkIcon,
  Visibility as ViewIcon,
  CloudUpload as TalanaIcon,
  Replay as RetryIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { InfoTip } from '../LabelWithInfo';
import formatRut from '../../erp/formatRut';
import { formatDateTime, formatDateRange } from '../formatDate';
import ErpTalanaDocumentsDialog from './ErpTalanaDocumentsDialog';

const toDateLabel = (value) => formatDateTime(value); // DD/MM/AAAA HH:mm (CLAUDE.md → Fechas)

const JOB_POLL_MS = 3000;

/**
 * Exportación a remuneraciones (fase 4 + API Talana 2026-09-22). Flujo
 * principal: seleccionar pendientes → **Enviar a Talana** (POST
 * /erp/contracts/export-talana → job en segundo plano con resultado por
 * registro, polling cada 3 s). Respaldo: descargar el Excel de carga y marcar
 * como exportados a mano. La segunda tab muestra los modificados post-export
 * con sus cambios; "Reenviar a Talana" hace el PATCH del contrato.
 * Antes de enviar (también al reintentar y reenviar) se abre la ventana de
 * documentos de cada persona (PLAN_DOCUMENTOS_TALANA §4.1): los marcados van
 * con el contrato y el panel muestra el resultado por documento.
 */
const ErpExportLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization, authToken, showAlert } = useStore.getState();

  const [currentTab, setCurrentTab] = useState(0);
  const [pending, setPending] = useState([]);
  const [modified, setModified] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [changesOpen, setChangesOpen] = useState(null); // item de modified en detalle
  const [job, setJob] = useState(null); // último envío a Talana (o el que está corriendo)
  const [talanaInfo, setTalanaInfo] = useState({ configured: true, environment: null });
  // Ventana de documentos: { mode: 'export', contractIds } | { mode: 'documents', employeeRecordId, preselectKeys }
  const [docsDialog, setDocsDialog] = useState(null);
  const pollRef = useRef(null);

  const headers = { headers: { Authorization: `Bearer ${authToken}` } };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [pendingRes, modifiedRes] = await Promise.all([
        api.get('/erp/contracts', {
          params: { organizationId: organization, exportStatus: 'pending' },
          ...headers,
        }),
        api.get('/erp/contracts/modified-after-export', {
          params: { organizationId: organization },
          ...headers,
        }),
      ]);
      setPending(pendingRes.data.response?.data || []);
      setModified(modifiedRes.data.response?.data || []);
      setSelected([]);
    } catch (err) {
      console.error(err);
      setError(t('erp.export.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadLastJob = async () => {
    try {
      const response = await api.get('/erp/contracts/export-talana/jobs', {
        params: { organizationId: organization, limit: 1 },
        ...headers,
      });
      // Los endpoints export-talana devuelven el objeto directo en response.data.response
      const body = response.data.response;
      if (body && body.jobs) {
        setTalanaInfo({ configured: body.configured !== false, environment: body.environment || null });
        if (body.jobs?.[0]) setJob(body.jobs[0]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (organization) {
      load();
      loadLastJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // Polling mientras el envío a Talana está en curso
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!job || job.status !== 'running') return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const response = await api.get(`/erp/contracts/export-talana/jobs/${job.uid}`, {
          params: { organizationId: organization },
          ...headers,
        });
        const next = response.data.response;
        if (next && next.uid) {
          setJob(next);
          if (next.status !== 'running') {
            clearInterval(pollRef.current);
            const docs = next.documents || {};
            const docsText = docs.total > 0 ? ` ${t('erp.export.talana.docs.finished', { sent: docs.sent, failed: docs.failed })}` : '';
            showAlert(
              `${t('erp.export.talana.finished', { ok: next.ok, failed: next.failed, total: next.total })}${docsText}`,
              next.failed > 0 || docs.failed > 0 ? 'warning' : 'success'
            );
            load();
          }
        }
      } catch (err) {
        console.error(err);
      }
    }, JOB_POLL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.uid, job?.status]);

  // documents: { contractId: [{ key, categoria, puedeVerloElTrabajador }] } de la ventana de documentos
  const handleSendTalana = async (contractIds, documents = {}) => {
    const docsCount = Object.values(documents).reduce((sum, list) => sum + list.length, 0);
    try {
      setWorking(true);
      const response = await api.post(
        '/erp/contracts/export-talana',
        { organizationId: organization, contractIds, ...(docsCount > 0 ? { documents } : {}) },
        headers
      );
      const started = response.data.response;
      if (started && started.uid) {
        setJob(started);
        setSelected([]);
        showAlert(
          docsCount > 0
            ? t('erp.export.talana.docs.started', { count: contractIds.length, docs: docsCount })
            : t('erp.export.talana.started', { count: contractIds.length }),
          'info'
        );
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.status;
      if (code === 503) showAlert(t('erp.talana.notConfigured'), 'warning');
      else if (code === 409) showAlert(t('erp.export.talana.alreadyRunning'), 'info');
      else showAlert(t('erp.export.talana.startError'), 'error');
    } finally {
      setWorking(false);
    }
  };

  const toggle = (uid) =>
    setSelected((prev) => (prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]));

  const toggleAll = () =>
    setSelected((prev) => (prev.length === pending.length ? [] : pending.map((c) => c.uid)));

  const handleDownload = async (contractIds) => {
    try {
      setWorking(true);
      const response = await api.post(
        '/erp/contracts/export-file',
        { organizationId: organization, contractIds },
        { ...headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `carga_remuneraciones_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      showAlert(t('erp.export.downloadError'), 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleMarkExported = async (contractIds) => {
    try {
      setWorking(true);
      const response = await api.post(
        '/erp/contracts/export',
        { organizationId: organization, contractIds },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        showAlert(
          t('erp.export.markSuccess', { count: response.data.response.data?.count || contractIds.length }),
          'success'
        );
        load();
      }
    } catch (err) {
      console.error(err);
      showAlert(t('erp.export.markError'), 'error');
    } finally {
      setWorking(false);
    }
  };

  const jobRunning = job?.status === 'running';

  // Ventana de documentos antes de enviar contratos (enviar, reintentar, reenviar)
  const openSendDialog = (contractIds) => setDocsDialog({ mode: 'export', contractIds });

  const handleConfirmExport = (documents) => {
    const contractIds = docsDialog?.contractIds || [];
    setDocsDialog(null);
    handleSendTalana(contractIds, documents);
  };

  // Documentos que vale la pena reintentar (no los que ya estaban o iban en otro envío)
  const retryableDocuments = (item) => (item.documents || []).filter((d) => d.status === 'failed'
    || (d.status === 'skipped' && !['ALREADY_SENT', 'IN_PROGRESS'].includes(d.error?.code)));

  const documentsDetail = (item) => {
    if (!item.documentsTotal && !(item.documents || []).length) return null;
    const summary = item.documentsSummary;
    if (item.step === 'documents' && item.status === 'ok') {
      const done = (item.documents || []).filter((d) => d.status !== 'pending').length;
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('erp.export.talana.docs.progress', { done, total: item.documents?.length || item.documentsTotal })}
        </Typography>
      );
    }
    if (!summary) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('erp.export.talana.docs.pendingCount', { count: item.documentsTotal })}
        </Typography>
      );
    }
    const problems = (item.documents || []).filter((d) => d.error && d.status !== 'sent');
    return (
      <Tooltip title={problems.map((d) => `${d.name || d.key}: ${d.error.message}`).join(' · ')}>
        <Typography variant="caption" color={summary.failed > 0 ? 'error' : 'text.secondary'} sx={{ display: 'block' }}>
          {t('erp.export.talana.docs.summary', { sent: summary.sent, failed: summary.failed, skipped: summary.skipped })}
        </Typography>
      </Tooltip>
    );
  };

  const itemChip = (item) => {
    const map = {
      pending: { color: 'default', key: 'pending' },
      running: { color: 'info', key: 'running' },
      ok: { color: 'success', key: 'ok' },
      failed: { color: 'error', key: 'failed' },
      skipped: { color: 'default', key: 'skipped' },
    };
    const m = map[item.status] || map.pending;
    return (
      <Chip size="small" color={m.color} variant={item.status === 'ok' ? 'filled' : 'outlined'}
        icon={item.status === 'running' ? <CircularProgress size={12} color="inherit" /> : undefined}
        label={t(`erp.export.talana.itemStatus.${m.key}`)} />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>{t('erp.export.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('erp.export.subtitle')}
      </Typography>

      <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 2 }}>
        <Tab label={`${t('erp.export.tabs.pending')} (${pending.length})`} sx={{ textTransform: 'none' }} />
        <Tab label={`${t('erp.export.tabs.modified')} (${modified.length})`} sx={{ textTransform: 'none' }} />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : currentTab === 0 ? (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Tooltip title={talanaInfo.configured ? t('erp.export.talana.sendHint') : t('erp.talana.notConfigured')}>
              <span>
                <Button variant="contained" size="small" startIcon={<TalanaIcon />}
                  disabled={working || jobRunning || selected.length === 0 || !talanaInfo.configured}
                  onClick={() => openSendDialog(selected)}
                  sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
                  {t('erp.export.talana.sendSelected', { count: selected.length })}
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
              disabled={working || selected.length === 0} onClick={() => handleDownload(selected)}
              sx={{ textTransform: 'none' }}>
              {t('erp.export.downloadSelected', { count: selected.length })}
            </Button>
            <Tooltip title={t('erp.export.talana.markHint')}>
              <span>
                <Button variant="text" size="small" startIcon={<MarkIcon />}
                  disabled={working || selected.length === 0} onClick={() => handleMarkExported(selected)}
                  sx={{ textTransform: 'none' }}>
                  {t('erp.export.markSelected', { count: selected.length })}
                </Button>
              </span>
            </Tooltip>
            {talanaInfo.environment && (
              <Chip size="small" variant="outlined" color={talanaInfo.environment === 'production' ? 'success' : 'warning'}
                label={t(`erp.talana.environment.${talanaInfo.environment}`)} />
            )}
          </Box>

          {job && (
            <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>{t('erp.export.talana.jobTitle')}</Typography>
                {jobRunning && (
                  <Chip size="small" color="info" icon={<CircularProgress size={12} color="inherit" />}
                    label={t('erp.export.talana.running', { done: job.done, total: job.total })} />
                )}
                {job.stoppedReason && (
                  <Chip size="small" color="error" variant="outlined" label={t('erp.export.talana.stopped', { code: job.stoppedReason })} />
                )}
              </Box>
              {jobRunning && <LinearProgress variant="determinate" value={job.total ? (job.done / job.total) * 100 : 0} sx={{ mb: 1 }} />}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('erp.contracts.table.worker')}</TableCell>
                    <TableCell>{t('erp.contracts.table.rut')}</TableCell>
                    <TableCell>{t('erp.export.talana.state')}</TableCell>
                    <TableCell>{t('erp.export.talana.detail')}</TableCell>
                    <TableCell>{t('erp.export.talana.sentAt')}</TableCell>
                    <TableCell>{t('erp.export.talana.sentBy')}</TableCell>
                    <TableCell align="right">{t('erp.common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(job.items || []).map((item) => (
                    <TableRow key={item.contractId}>
                      <TableCell>{item.workerName || '—'}</TableCell>
                      <TableCell>{formatRut(item.workerRut)}</TableCell>
                      <TableCell>{itemChip(item)}</TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        {item.status === 'ok' && (
                          <Typography variant="caption" color="text.secondary">
                            Talana · persona {item.personaId} · contrato {item.contratoId}
                          </Typography>
                        )}
                        {item.error && (
                          <Tooltip title={item.error.missing ? item.error.missing.join(', ') : JSON.stringify(item.error.body || '')}>
                            <Typography variant="caption" color="error" sx={{ display: 'block', whiteSpace: 'normal' }}>
                              {item.error.code ? `${item.error.code}: ` : ''}{item.error.message}
                            </Typography>
                          </Tooltip>
                        )}
                        {item.status === 'running' && item.step && (
                          <Typography variant="caption" color="text.secondary">{t(`erp.export.talana.steps.${item.step}`)}</Typography>
                        )}
                        {documentsDetail(item)}
                      </TableCell>
                      <TableCell>{toDateLabel(item.finishedAt || item.startedAt || job.startedAt)}</TableCell>
                      <TableCell>{job.startedBy?.name || job.startedBy?.email || '—'}</TableCell>
                      <TableCell align="right">
                        {(item.status === 'failed' || item.status === 'skipped') && !jobRunning && (
                          <Button size="small" startIcon={<RetryIcon />} sx={{ textTransform: 'none' }}
                            disabled={working} onClick={() => openSendDialog([item.contractId])}>
                            {t('erp.export.talana.retry')}
                          </Button>
                        )}
                        {item.status === 'ok' && !jobRunning && item.employeeRecordId && retryableDocuments(item).length > 0 && (
                          <Button size="small" startIcon={<RetryIcon />} sx={{ textTransform: 'none' }}
                            disabled={working || !talanaInfo.configured}
                            onClick={() => setDocsDialog({
                              mode: 'documents',
                              employeeRecordId: item.employeeRecordId,
                              preselectKeys: retryableDocuments(item).map((d) => d.key),
                            })}>
                            {t('erp.export.talana.docs.retry')}
                          </Button>
                        )}
                        <Button size="small" startIcon={<ViewIcon />} sx={{ textTransform: 'none' }}
                          onClick={() => navigate(`/erp/contracts/${item.contractId}`)}>
                          {t('erp.common.view')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {pending.length === 0 ? (
            <Alert severity="success">{t('erp.export.noPending')}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox size="small"
                        checked={selected.length === pending.length && pending.length > 0}
                        indeterminate={selected.length > 0 && selected.length < pending.length}
                        onChange={toggleAll} />
                    </TableCell>
                    <TableCell>{t('erp.contracts.table.worker')}</TableCell>
                    <TableCell>{t('erp.contracts.table.rut')}</TableCell>
                    <TableCell>{t('erp.contracts.table.client')}</TableCell>
                    <TableCell>{t('erp.contracts.table.dates')}</TableCell>
                    <TableCell align="right">{t('erp.common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pending.map((contract) => (
                    <TableRow key={contract.uid} hover>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.includes(contract.uid)}
                          onChange={() => toggle(contract.uid)} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{contract.workerName || '—'}</TableCell>
                      <TableCell>{formatRut(contract.workerRut)}</TableCell>
                      <TableCell>{contract.clientName || '—'}</TableCell>
                      <TableCell>{formatDateRange(contract.dates?.startDate, contract.dates?.endDate, t('erp.contracts.openEnded'))}</TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<ViewIcon />} sx={{ textTransform: 'none' }}
                          onClick={() => navigate(`/erp/contracts/${contract.uid}`)}>
                          {t('erp.common.view')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        <>
          {modified.length === 0 ? (
            <Alert severity="success">{t('erp.export.noModified')}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('erp.contracts.table.worker')}</TableCell>
                    <TableCell>{t('erp.contracts.table.rut')}</TableCell>
                    <TableCell>{t('erp.export.changesCount')}</TableCell>
                    <TableCell align="right">{t('erp.common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modified.map((item) => (
                    <TableRow key={item.contract.uid} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{item.contract.workerName || '—'}</TableCell>
                      <TableCell>{formatRut(item.contract.workerRut)}</TableCell>
                      <TableCell>
                        <Chip size="small" color="error" label={item.changes.length} />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setChangesOpen(item)}>
                          {t('erp.export.viewChanges')}
                        </Button>
                        <Button size="small" startIcon={<TalanaIcon />} sx={{ textTransform: 'none' }}
                          disabled={working || jobRunning || !talanaInfo.configured}
                          onClick={() => openSendDialog([item.contract.uid])}>
                          {t('erp.export.talana.resend')}
                        </Button>
                        <Button size="small" sx={{ textTransform: 'none' }}
                          disabled={working} onClick={() => handleMarkExported([item.contract.uid])}>
                          {t('erp.export.reExport')}
                        </Button>
                        <InfoTip title={t('erp.tooltips.export.reExport')} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={Boolean(changesOpen)} onClose={() => setChangesOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('erp.export.changesTitle')} — {changesOpen?.contract?.workerName || changesOpen?.contract?.workerRut}
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('erp.export.changeSource')}</TableCell>
                <TableCell>{t('erp.contracts.changeLog.field')}</TableCell>
                <TableCell>{t('erp.contracts.changeLog.oldValue')}</TableCell>
                <TableCell>{t('erp.contracts.changeLog.newValue')}</TableCell>
                <TableCell>{t('erp.contracts.changeLog.changedAt')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(changesOpen?.changes || []).map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Chip size="small" variant="outlined"
                      label={entry.source === 'contract' ? t('erp.contracts.title') : t('erp.employees.title')} />
                  </TableCell>
                  <TableCell>{entry.field}</TableCell>
                  <TableCell>{String(entry.oldValue ?? '—')}</TableCell>
                  <TableCell>{String(entry.newValue ?? '—')}</TableCell>
                  <TableCell>{toDateLabel(entry.changedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangesOpen(null)} sx={{ textTransform: 'none' }}>{t('erp.common.cancel')}</Button>
        </DialogActions>
      </Dialog>

      <ErpTalanaDocumentsDialog
        open={Boolean(docsDialog)}
        onClose={() => setDocsDialog(null)}
        mode={docsDialog?.mode || 'export'}
        contractIds={docsDialog?.contractIds || []}
        employeeRecordId={docsDialog?.employeeRecordId || null}
        preselectKeys={docsDialog?.preselectKeys || null}
        onConfirmExport={handleConfirmExport}
      />
    </Box>
  );
};

export default ErpExportLayout;
